import { Controller } from "@hotwired/stimulus"
import { Offcanvas } from "bootstrap"
import Sortable from "sortablejs"
import { captureBackdropDrops, carriesFiles, flashFileWarning, partitionByAccept } from "../utils/file-upload.util"
import { refreshWhenIdle } from "../utils/turbo-refresh.util"

// Transient upload failures worth an automatic retry (LUC-698): the connection
// died mid-request (deploy restart, dropped wifi — status 0) or a proxy/app
// answered "come back later". Everything else (4xx validation, quota, 500) is
// deterministic — retrying it just burns time.
const TRANSIENT_HTTP_STATUSES = [0, 408, 425, 502, 503, 504]

// Auto-retry ladder in ms. Deliberately short: a deploy restart takes seconds
// and a longer wait reads as a hung UI. Whatever survives the ladder falls
// through to the manual "try again" tail, with the files still held in memory.
const RETRY_DELAYS_MS = [1000, 3000]

// Fallbacks for the server-side copy passed in through the `translations`
// value; a key the view did not send never reads as `undefined`.
const DEFAULT_TRANSLATIONS = {
  creatingText: "Creating...",
  doneText: "Done",
  errorText: "Upload failed",
  retryText: "Try again",
  retryingText: "Retrying...",
  leaveWarningText: "Some uploads did not finish yet.",
  emptyFileText: "File is empty (0 bytes) and was skipped: ",
  unsupportedFileText: "Unsupported file type and was skipped: ",
  groupMinFilesText: "A group needs at least 2 files."
}

// Connects to data-controller="attachments"
export default class extends Controller {
  static targets = [
    "attachmentsForm",
    "attachments",
    "previewList",
    "uploadArea",
    "submitButton",
    "filesOrigin",
    "attachmentsPreview",
    "groupButton",
    "groupHint"
  ]

  static values = {
    translations: { type: Object, default: {} },
    bulkEnabled: { type: Boolean, default: true },
    // Document group (LUC-304): every picked file becomes ONE expense, merged
    // server-side into a single PDF before extraction.
    groupUrl: { type: String, default: "" },
    // Set by expenses?upload=1, where the phone welcome's "Odfotiť prvý bloček" leads.
    openOnConnect: { type: Boolean, default: false },
    // "auto" on the dashboard's universal upload: the server routes each file
    // by its supplier (our own invoice vs. an expense) once it is extracted.
    intake: { type: String, default: "" }
  }

  // A group of one is just an upload — the regular path handles that better.
  static MIN_GROUP_FILES = 2

  get #texts() {
    return { ...DEFAULT_TRANSLATIONS, ...this.translationsValue }
  }

  connect() {
    this._accumulatedFiles = new DataTransfer()
    this._failedTasks = []
    this._completedCount = 0
    this._groupMode = false
    this._sortable = null
    // File -> idempotency token. Keyed by the File object so the token survives
    // re-submitting the same selection, not just an in-run retry.
    this._uploadTokens = new WeakMap()
    // Upload tokens of the pass in flight; refilled by #runUploads.
    this._runTokens = []

    // Capture original button HTML once on connect (guaranteed clean state)
    if (this.hasSubmitButtonTarget) {
      this._originalButtonHTML = this.submitButtonTarget.innerHTML
    }

    // Always reset sidebar state when hidden (covers auto-close, X button, cancel, backdrop)
    this._hiddenHandler = () => {
      this.resetSidebar()
      this.#releaseHeldRefresh()
    }
    this.element.addEventListener("hidden.bs.offcanvas", this._hiddenHandler)

    // A page refresh broadcast to this page (pairing an uploaded expense to a bank
    // transaction sends one) replaces the body, and the panel with it. The run would
    // carry on in memory with nothing left on screen: no progress, no retry for a
    // file that fails, no beforeunload guard. Hold it until the panel is done with.
    this._heldRefresh = false
    this._refreshGuard = (event) => {
      if (event.detail.newStream?.getAttribute("action") !== "refresh" || !this.#panelInUse()) return

      event.preventDefault()
      this._heldRefresh = true
    }
    document.addEventListener("turbo:before-stream-render", this._refreshGuard)

    // Set extract checkbox based on which button opened the sidebar (only if explicitly set)
    this._showHandler = (event) => {
      const trigger = event.relatedTarget
      if (trigger && "extract" in trigger.dataset) {
        const extract = trigger.dataset.extract === "true"
        const toggle = document.getElementById("extract_data_toggle")
        const hidden = document.getElementById("extract_data_hidden")
        if (toggle) toggle.checked = extract
        if (hidden) hidden.checked = extract
      }
    }
    this.element.addEventListener("show.bs.offcanvas", this._showHandler)

    // Guard the tab while uploads are in flight or waiting to be retried — the
    // File objects only live in memory, so navigating away really does lose them.
    this._beforeUnloadHandler = (event) => {
      if (!this.isSubmitting && this._failedTasks.length === 0) return

      event.preventDefault()
      event.returnValue = this.#texts.leaveWarningText
      return this.#texts.leaveWarningText
    }
    window.addEventListener("beforeunload", this._beforeUnloadHandler)

    this._releaseBackdropDrops = captureBackdropDrops({
      element: this.element,
      enabled: () => this.#canTakeFiles(),
      onFiles: (files) => this.addFiles(files),
      setDragging: (active) => this.#setDragging(active)
    })

    const files = this.hasFilesOriginTarget ? this.filesOriginTarget : false

    if (files) {
      // Only copy to main form if we have the attachments target (main form)
      if (this.hasAttachmentsTarget) {
        this.attachmentsTarget.files = files.files
      }

      // Also copy to main form's hidden input if it exists (for sidebar controller)
      const hiddenInput = document.getElementById("hidden_attachments_input")
      if (hiddenInput && !this.hasAttachmentsTarget) {
        hiddenInput.files = files.files
      }

      this._filesChangeHandler = this.handleFiles.bind(this)
      files.addEventListener("change", this._filesChangeHandler, false)
    }

    if (this.openOnConnectValue) this.#openOnce()
  }

  disconnect() {
    this.#teardownSortable()
    this._releaseBackdropDrops?.()
    this._cancelRefresh?.()
    // A run outlives its panel when the member closes it and moves on. Its end must
    // not refresh the page they are on by then, which this refresh was never meant for.
    this._heldRefresh = false
    document.removeEventListener("turbo:before-stream-render", this._refreshGuard)
    if (this._beforeUnloadHandler) {
      window.removeEventListener("beforeunload", this._beforeUnloadHandler)
    }
    if (this._showHandler) {
      this.element.removeEventListener("show.bs.offcanvas", this._showHandler)
    }
    if (this._hiddenHandler) {
      this.element.removeEventListener("hidden.bs.offcanvas", this._hiddenHandler)
    }
    if (this.hasFilesOriginTarget) {
      const files = this.filesOriginTarget
      if (files && this._filesChangeHandler) {
        files.removeEventListener("change", this._filesChangeHandler, false)
      }
    }
  }

  // Bootstrap's classes are on the panel from the opening animation (`showing`) for
  // as long as it is open, picked files or a retry waiting. The sidebar instances of
  // this controller never get them.
  #panelInUse() {
    const { classList } = this.element
    return this.isSubmitting || classList.contains("show") || classList.contains("showing")
  }

  #releaseHeldRefresh() {
    if (!this._heldRefresh || this.#panelInUse()) return

    this._heldRefresh = false
    this._cancelRefresh?.()
    this._cancelRefresh = refreshWhenIdle(window.location.href)
  }

  handleFiles() {
    if (!this.hasFilesOriginTarget) return

    this.addFiles(Array.from(this.filesOriginTarget.files))
  }

  // Highlight the panel while a file is hovering over it. Dropping is handled
  // on the whole offcanvas, not just the artwork, so a near-miss can't fall
  // through to the browser default (see #drop).
  dragOver(event) {
    if (!this.#acceptsDrop(event)) return

    event.preventDefault()
    // Without this the cursor can read "move"/"link" when a modifier is held,
    // promising something other than the copy that actually happens.
    if (event.dataTransfer) event.dataTransfer.dropEffect = "copy"
    this.element.classList.add("file-drop--active")
  }

  dragLeave(event) {
    // dragleave also fires when the pointer crosses onto a child element —
    // only drop the highlight once it has really left the panel.
    if (event.relatedTarget && this.element.contains(event.relatedTarget)) return

    this.#setDragging(false)
  }

  // The dropzone copy promises "click to upload or DRAG files here", but the
  // only drop-capable element used to be the native <input type=file> itself —
  // a thin strip under the artwork. Dropping anywhere else fell through to the
  // browser default, which navigates the tab to the dropped file and takes the
  // offcanvas (plus every already-picked file) with it.
  drop(event) {
    if (!this.#acceptsDrop(event)) return

    event.preventDefault()
    this.element.classList.remove("file-drop--active")
    this.addFiles(Array.from(event.dataTransfer.files))
  }

  // Only claim drags that actually carry files — dragging selected text across
  // the panel must keep its normal behaviour — and only when this instance has
  // somewhere to put them. The locked variant of the offcanvas (no attachment
  // or extraction entitlement) renders the upgrade pitch with no file input;
  // claiming a drop there would highlight a panel that then eats the file.
  #acceptsDrop(event) {
    return this.#canTakeFiles() && carriesFiles(event)
  }

  // Not while a run is in flight: `_accumulatedFiles` is the queue that run is
  // working through, and anything added now is discarded by the reset that
  // follows it — after having re-enabled the footer button mid-upload.
  #canTakeFiles() {
    return this.hasFilesOriginTarget && !this.isSubmitting
  }

  #setDragging(active) {
    this.element.classList.toggle("file-drop--active", active)
  }

  // The one way files enter this controller, from the picker or from a drop.
  addFiles(incoming) {
    if (!this.hasFilesOriginTarget) return

    // A drop bypasses the input's `accept` filter entirely (and the picker can
    // be switched to "All files" on some platforms), so enforce it here for
    // both paths rather than letting the upload fail server-side.
    const { accepted, rejected } = partitionByAccept(incoming, this.filesOriginTarget.accept)
    let newFiles = accepted
    for (const file of rejected) {
      this.showUnsupportedFileNotice(file.name)
    }

    // Safari + iCloud Drive can hand us 0-byte File refs when the file is
    // still a cloud placeholder. Filter them out before they reach the
    // server — the user gets immediate feedback instead of a silently
    // broken expense + empty S3 object.
    const emptyFiles = newFiles.filter((file) => file.size === 0)
    if (emptyFiles.length > 0) {
      newFiles = newFiles.filter((file) => file.size > 0)
      for (const file of emptyFiles) {
        this.showEmptyFileNotice(file.name)
      }
    }

    // When bulk upload is disabled, limit to single file (replace previous).
    // Guarded on newFiles.length: without it, a drop where every file was
    // filtered out above would clear the selection the user already had and
    // leave the panel empty with nothing rendered to re-pick from.
    if (!this.bulkEnabledValue && newFiles.length > 0) {
      newFiles = newFiles.slice(0, 1)
      this._accumulatedFiles = new DataTransfer()
      if (this.hasAttachmentsPreviewTarget) {
        this.attachmentsPreviewTarget.innerHTML = ""
      }
    }

    for (const file of newFiles) {
      this._accumulatedFiles.items.add(file)
    }

    // Sync accumulated files to the file input so submitAndClear reads them all
    this.#syncFileInputs()

    // Show preview only for newly added files (append, not replace)
    if (this.hasPreviewListTarget && newFiles.length > 0) {
      this.showFilePreviewWithTurbo(newFiles)
      this.hideUploadArea()
    }

    // Picking more files after a partial failure puts the footer button back in
    // "create" mode, so the new files can't get stranded behind the retry state.
    // Re-submitting is safe: tokens are per-File, so files that already landed
    // resolve to their existing expense instead of a duplicate.
    if (newFiles.length > 0 && this._failedTasks.length > 0) {
      this.restoreSubmitButton()
    }

    this.updateSubmitButtonState()
  }

  async showFilePreviewWithTurbo(files) {
    // Prepare file data for server
    const filesData = Array.from(files).map((file) => ({
      name: file.name,
      size: file.size,
      type: file.type
    }))

    try {
      // Send file info to server via Turbo Streams
      const currentPath = window.location.pathname

      // Extract workspace ID from URL
      // Expected format: /:workspace_id/:document_type/:id/edit or /:workspace_id/expenses
      const pathParts = currentPath.split("/").filter((part) => part !== "")
      const workspaceId = pathParts[0]

      // Use the shared preview URL for both documents and expenses
      const previewUrl = `/${workspaceId}/preview_attachments`

      const response = await fetch(previewUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector('[name="csrf-token"]').content,
          Accept: "text/vnd.turbo-stream.html"
        },
        body: JSON.stringify({ files: filesData })
      })

      if (response.ok) {
        const turboStream = await response.text()
        // Append leaves server-rendered empty state in place; remove it before adding previews
        if (files.length > 0) {
          document.querySelector("#attachments_preview .attachments__empty")?.remove()
        }
        Turbo.renderStreamMessage(turboStream)
      } else {
        console.error("Server error:", response.status, response.statusText)
      }
    } catch (error) {
      console.error("Error previewing files:", error)
    }
  }

  removePreview(event) {
    const previewId = event.currentTarget.dataset.previewId

    if (previewId) {
      // Remove the specific preview item from DOM
      const previewItem = document.querySelector(`[data-preview-id="${previewId}"]`)
      if (previewItem) {
        // Remove file from accumulated list by DOM position (handles duplicate filenames)
        const allPreviewItems = Array.from(document.querySelectorAll(".attachments__item--preview[data-preview-id]"))
        const index = allPreviewItems.indexOf(previewItem)
        if (index >= 0 && index < this._accumulatedFiles.files.length) {
          const newDt = new DataTransfer()
          for (let i = 0; i < this._accumulatedFiles.files.length; i++) {
            if (i === index) continue
            newDt.items.add(this._accumulatedFiles.files[i])
          }
          this._accumulatedFiles = newDt
          this.#syncFileInputs()
        }

        previewItem.remove()

        // Check if there are any remaining preview items
        const remainingPreviewItems = document.querySelectorAll("[data-preview-id]")

        if (remainingPreviewItems.length === 0) {
          // Show empty state when no preview items remain using Turbo Streams
          this.showEmptyState()
        }

        this.updateSubmitButtonState()
      }
    }
  }

  showEmptyState() {
    // Send empty files array to server to get empty state via Turbo Streams
    this.showFilePreviewWithTurbo([])
    this.showUploadArea()
  }

  showUploadArea() {
    if (this.hasUploadAreaTarget) {
      this.uploadAreaTarget.classList.remove("d-none")
    }
  }

  hideUploadArea() {
    if (this.hasUploadAreaTarget) {
      this.uploadAreaTarget.classList.add("d-none")
    }
  }

  // Update submit button state based on files
  updateSubmitButtonState() {
    if (!this.hasSubmitButtonTarget) return

    const hasFiles = this._accumulatedFiles && this._accumulatedFiles.files.length > 0

    this.submitButtonTarget.disabled = !hasFiles
  }

  // Upload files sequentially with per-file progress tracking, auto-retry of
  // transient failures and a manual retry tail for whatever still didn't land.
  async submitAndClear(_event) {
    if (this.isSubmitting) return

    const files = Array.from(this.filesOriginTarget.files)
    if (files.length === 0) return

    const previewItems = this.hasAttachmentsPreviewTarget
      ? Array.from(this.attachmentsPreviewTarget.querySelectorAll(":scope > .attachments__item--preview"))
      : []

    if (this._groupMode) {
      if (files.length < this.constructor.MIN_GROUP_FILES) {
        flashFileWarning(this.#texts.groupMinFilesText)
        return
      }
      await this.#runGroupUpload(files, previewItems)
      return
    }

    this._completedCount = 0
    this._failedTasks = []
    this._totalFiles = files.length

    const tasks = files.map((file, i) => ({
      file,
      previewItem: previewItems[i] || null,
      index: i + 1,
      // Stable per-file identity, reused verbatim by every retry — that's what
      // lets the server recognise a resend and reuse the expense it already
      // created instead of duplicating it (and double-charging the AI quota).
      token: this.#tokenFor(file)
    }))

    await this.#runUploads(tasks)
  }

  // Retry every file that is still failed (footer button)
  async retryFailed(event) {
    // A group is one indivisible request — re-submit the whole thing. The group
    // token is stable across retries, so a resend of an upload the server had
    // already committed reuses that expense instead of creating a second group.
    if (this._groupMode) {
      await this.submitAndClear(event)
      return
    }

    await this.#runUploads([...this._failedTasks])
  }

  // Retry a single failed file (per-item button in the error state)
  async retryItem(event) {
    event.preventDefault()

    const previewItem = event.currentTarget.closest(".attachments__item--preview")
    const task = this._failedTasks.find((failed) => failed.previewItem === previewItem)
    if (!task) return

    await this.#runUploads([task])
  }

  // Runs one upload pass over the given tasks. Callable repeatedly: `_completedCount`
  // is cumulative and `_failedTasks` is the authoritative "still not uploaded" set,
  // so retrying one file doesn't lose track of the others.
  async #runUploads(tasks) {
    if (this.isSubmitting || tasks.length === 0) return
    this.isSubmitting = true

    const extractData = document.getElementById("extract_data_hidden")?.checked ? "1" : "0"
    const uploadUrl = this.attachmentsFormTarget.action
    const csrfToken = document.querySelector('[name="csrf-token"]').content

    // The tasks in this pass are no longer "failed" until they fail again
    this._failedTasks = this._failedTasks.filter((failed) => !tasks.includes(failed))
    this._lastSuccessResponse = null
    this._lastRefusalResponse = null
    this._runTotal = tasks.length
    // Every idempotency token in this pass, sent with each request. Only the
    // final one uses them (LUC-359): this method renders ONLY the last response's
    // stream, so the duplicate-file warning has to be decided over the whole run
    // rather than over the single file that happened to go last.
    this._runTokens = tasks.map((task) => task.token).filter(Boolean)

    this.submitButtonTarget.disabled = true
    this.submitButtonTarget.classList.remove("btn-warning")
    this.submitButtonTarget.classList.add("btn-primary")
    this.updateUploadCounter()

    // Show uploading state for all files in this pass
    tasks.forEach((task, i) => {
      task.index = i + 1
      if (task.previewItem) {
        this.clearItemError(task.previewItem)
        this.setItemUploading(task.previewItem)
      }
    })

    // Upload sequentially to prevent race conditions with usage limit checks
    for (const task of tasks) {
      await this.processUploadTask(task, extractData, uploadUrl, csrfToken)
    }

    // Apply the last successful turbo stream to update the expenses table.
    // When nothing landed, show the last refusal's stream instead (a 422 whose
    // toast names the reason), so a fully failed run is not silent. A mixed
    // run keeps the success stream. Either way the failed items stay red.
    const finalStream = this._lastSuccessResponse || this._lastRefusalResponse
    if (finalStream) this.#renderRunStream(finalStream)

    this.isSubmitting = false
    this.submitButtonTarget.disabled = false
    this.#releaseHeldRefresh()

    // Something is still missing — keep the sidebar OPEN and offer a retry.
    // Auto-closing here is exactly how uploads used to disappear silently
    // during a deploy: the files only live in this controller's memory.
    if (this._failedTasks.length > 0) {
      this.submitButtonTarget.classList.remove("btn-primary")
      this.submitButtonTarget.classList.add("btn-warning")
      this.submitButtonTarget.innerHTML =
        `<span class="material-symbols-outlined me-1" style="font-size: 18px;">refresh</span> ` +
        `${this.#escapeHtml(this.#texts.retryText)} (${this._failedTasks.length})`
      this.submitButtonTarget.setAttribute("data-action", "click->attachments#retryFailed")
      return
    }

    // Transform submit button to "Done" with upload summary
    const doneLabel = `${this.#escapeHtml(this.#texts.doneText)} ${this._completedCount} / ${this._totalFiles}`
    this.submitButtonTarget.innerHTML = `<span class="material-symbols-outlined me-1" style="font-size: 18px;">check</span> ${doneLabel}`
    this.submitButtonTarget.setAttribute("data-action", "click->attachments#submitAndClear")
    // Auto-close sidebar after 1 second
    setTimeout(() => this.closeSidebar(), 1000)
  }

  // Renders the run's final stream, then puts every file that is still failed
  // back into its error state. The footer keeps offering "Try again (n)" for
  // those files, so whatever the stream re-renders must not turn one of them
  // back into "ready to upload". Turbo dispatches turbo:before-stream-render
  // synchronously while the message is appended, so the listener sees exactly
  // this message's streams and wraps their render.
  #renderRunStream(html) {
    const failed = [...this._failedTasks]
    const keepErrors = (event) => {
      const render = event.detail.render
      event.detail.render = async (stream) => {
        await render(stream)
        this.#markFailed(failed)
      }
    }

    document.addEventListener("turbo:before-stream-render", keepErrors)
    try {
      Turbo.renderStreamMessage(html)
    } finally {
      document.removeEventListener("turbo:before-stream-render", keepErrors)
    }
  }

  #markFailed(tasks) {
    for (const task of tasks) {
      if (!this._failedTasks.includes(task)) continue

      const previewItem = this.#livePreviewItem(task)
      if (previewItem) this.setItemError(previewItem)
    }
  }

  // The task's preview item, or the one that replaced it under the same
  // preview id when a render swapped the node out.
  #livePreviewItem(task) {
    if (!task.previewItem || task.previewItem.isConnected) return task.previewItem
    if (!this.hasAttachmentsPreviewTarget) return null

    const previewId = task.previewItem.dataset.previewId
    const replacement = Array.from(
      this.attachmentsPreviewTarget.querySelectorAll(":scope > .attachments__item--preview")
    ).find((item) => item.dataset.previewId === previewId)
    if (replacement) task.previewItem = replacement
    return replacement || null
  }

  // --- Document group (LUC-304) -------------------------------------------

  // Turn group mode on/off. In group mode the picked files describe ONE
  // document, are posted in a single request, and are merged server-side into
  // one PDF before extraction — so one group costs one AI extraction, not N.
  toggleGroupMode(_event) {
    if (this.isSubmitting) return

    this._groupMode = !this._groupMode
    this.#renderGroupMode()
  }

  #renderGroupMode() {
    for (const button of this.groupButtonTargets) {
      button.classList.toggle("btn-secondary", !this._groupMode)
      button.classList.toggle("btn-primary", this._groupMode)
      button.setAttribute("aria-pressed", String(this._groupMode))
    }

    if (this.hasGroupHintTarget) {
      this.groupHintTarget.classList.toggle("d-none", !this._groupMode)
    }

    if (this._groupMode) {
      this.#setupSortable()
    } else {
      this.#teardownSortable()
    }
  }

  // Page order is the order of the preview list, so in group mode that list has
  // to be reorderable — a two-shot photo of an invoice is useless with the pages
  // the wrong way round.
  #setupSortable() {
    if (this._sortable || !this.hasAttachmentsPreviewTarget) return

    this._sortable = Sortable.create(this.attachmentsPreviewTarget, {
      animation: 150,
      draggable: ".attachments__item--preview",
      onEnd: (event) => this.#reorderFiles(event.oldIndex, event.newIndex)
    })
  }

  #teardownSortable() {
    this._sortable?.destroy()
    this._sortable = null
  }

  // Keeps the in-memory file list in step with the DOM order the user dragged
  // into place. Everything downstream (submit, preview removal) indexes by
  // position, so the two must never diverge.
  #reorderFiles(from, to) {
    if (from === undefined || to === undefined || from === to) return

    const files = Array.from(this._accumulatedFiles.files)
    if (from < 0 || from >= files.length || to < 0 || to >= files.length) return

    const [moved] = files.splice(from, 1)
    files.splice(to, 0, moved)

    const reordered = new DataTransfer()
    for (const file of files) {
      reordered.items.add(file)
    }
    this._accumulatedFiles = reordered
    this.#syncFileInputs()
  }

  // One request carrying every file of the group, in order.
  async #runGroupUpload(files, previewItems) {
    this.isSubmitting = true
    this._completedCount = 0
    this._failedTasks = []
    this._totalFiles = files.length

    this.submitButtonTarget.disabled = true
    this.submitButtonTarget.classList.remove("btn-warning")
    this.submitButtonTarget.classList.add("btn-primary")
    this.updateUploadCounter()

    for (const previewItem of previewItems) {
      this.clearItemError(previewItem)
      this.setItemUploading(previewItem)
    }

    try {
      const responseText = await this.#uploadGroup(files, previewItems[0] || null)
      this._heldRefresh = false
      this._completedCount = files.length
      for (const previewItem of previewItems) {
        this.setItemComplete(previewItem)
      }
      Turbo.renderStreamMessage(responseText)
      this.#finishGroupUpload(files.length)
    } catch (uploadError) {
      console.error("Document group upload failed:", uploadError)
      // Marked as one unit — retrying a single page of a group is meaningless,
      // so the per-item retry link is deliberately suppressed here.
      for (const previewItem of previewItems) {
        this.setItemError(previewItem, { retryable: false })
      }
      this._failedTasks = [{ group: true, files, previewItems }]
      this.#showGroupRetry()
    } finally {
      this.isSubmitting = false
      this.submitButtonTarget.disabled = false
      this.#releaseHeldRefresh()
    }
  }

  #finishGroupUpload(count) {
    const doneLabel = `${this.#escapeHtml(this.#texts.doneText)} ${count} / ${count}`
    this.submitButtonTarget.innerHTML = `<span class="material-symbols-outlined me-1" style="font-size: 18px;">check</span> ${doneLabel}`
    this.submitButtonTarget.setAttribute("data-action", "click->attachments#submitAndClear")
    setTimeout(() => this.closeSidebar(), 1000)
  }

  #showGroupRetry() {
    this.submitButtonTarget.classList.remove("btn-primary")
    this.submitButtonTarget.classList.add("btn-warning")
    this.submitButtonTarget.innerHTML =
      `<span class="material-symbols-outlined me-1" style="font-size: 18px;">refresh</span> ` +
      `${this.#escapeHtml(this.#texts.retryText)}`
    this.submitButtonTarget.setAttribute("data-action", "click->attachments#retryFailed")
  }

  #uploadGroup(files, progressItem) {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest()
      const formData = new FormData()

      for (const file of files) {
        formData.append("files[]", file)
      }
      formData.append("extract_data", document.getElementById("extract_data_hidden")?.checked ? "1" : "0")
      formData.append("upload_token", this.#groupToken())

      xhr.upload.addEventListener("progress", (event) => {
        if (event.lengthComputable && progressItem) {
          this.updateItemProgress(progressItem, Math.round((event.loaded / event.total) * 100))
        }
      })

      xhr.addEventListener("load", () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve(xhr.responseText)
        } else {
          reject(this.#uploadError(`${xhr.status}`, xhr.status))
        }
      })
      xhr.addEventListener("error", () => reject(this.#uploadError("Network error", 0)))
      xhr.addEventListener("timeout", () => reject(this.#uploadError("Timeout", 0)))
      xhr.addEventListener("abort", () => reject(this.#uploadError("Aborted", null)))

      xhr.open("POST", this.groupUrlValue)
      xhr.setRequestHeader("X-CSRF-Token", document.querySelector('[name="csrf-token"]').content)
      xhr.setRequestHeader("Accept", "text/vnd.turbo-stream.html")
      xhr.send(formData)
    })
  }

  // One token for the whole group, minted once per selection: a resend after a
  // dropped connection must reuse the expense the killed request committed
  // rather than create (and charge for) a second group.
  #groupToken() {
    if (!this._groupToken) {
      this._groupToken = this.#newUploadToken()
    }
    return this._groupToken
  }

  async processUploadTask(task, extractData, uploadUrl, csrfToken) {
    for (let attempt = 0; attempt <= RETRY_DELAYS_MS.length; attempt++) {
      try {
        const responseText = await this.uploadSingleFile(
          task.file,
          extractData,
          uploadUrl,
          csrfToken,
          task.previewItem,
          this._runTotal,
          task.index,
          task.token
        )
        this._lastSuccessResponse = responseText
        // This response carries the table as of now, so a refresh held so far has
        // nothing left to do and running it on close would only wipe the toast. One
        // held after it stays held: the table is rendered at the end of the run, but
        // from this response. A refusal carries no table.
        this._heldRefresh = false
        this._completedCount++
        if (task.previewItem) {
          this.setItemComplete(task.previewItem)
        }
        this.updateUploadCounter()
        return
      } catch (uploadError) {
        const willRetry = uploadError.transient && attempt < RETRY_DELAYS_MS.length
        console.error(
          `Upload failed for file ${task.index}/${this._runTotal} (${task.file.name})${willRetry ? " — retrying" : ""}:`,
          uploadError
        )
        if (!willRetry) {
          // A refused file (422) carries a turbo stream whose toast says why.
          if (uploadError.status === 422 && uploadError.body) {
            this._lastRefusalResponse = uploadError.body
          }
          break
        }

        if (task.previewItem) {
          this.setItemRetrying(task.previewItem)
        }
        await this.#sleep(RETRY_DELAYS_MS[attempt])
        if (task.previewItem) {
          this.setItemUploading(task.previewItem)
        }
      }
    }

    this._failedTasks.push(task)
    if (task.previewItem) {
      this.setItemError(task.previewItem)
    }
    this.updateUploadCounter()
  }

  updateUploadCounter() {
    const done = this._completedCount + this._failedTasks.length
    this.submitButtonTarget.innerHTML =
      `<span class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>` +
      `${this.#texts.creatingText} ${done} / ${this._totalFiles}`
  }

  // Upload a single file via XHR (supports upload progress events)
  uploadSingleFile(file, extractData, uploadUrl, csrfToken, previewItem, totalCount, currentIndex, uploadToken) {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest()
      const formData = new FormData()
      formData.append("files[]", file)
      formData.append("extract_data", extractData)
      formData.append("batch_total", totalCount.toString())
      formData.append("batch_index", currentIndex.toString())
      if (uploadToken) {
        formData.append("upload_token", uploadToken)
      }
      if (this.intakeValue) {
        formData.append("intake", this.intakeValue)
      }
      // Read off the controller rather than threaded through: this is run-wide
      // state, not per-file (see #runUploads).
      for (const token of this._runTokens || []) {
        formData.append("batch_tokens[]", token)
      }

      xhr.upload.addEventListener("progress", (e) => {
        if (e.lengthComputable && previewItem) {
          const percent = Math.round((e.loaded / e.total) * 100)
          this.updateItemProgress(previewItem, percent)
        }
      })

      xhr.addEventListener("load", () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve(xhr.responseText)
        } else {
          // Keep the body only when it is a turbo stream: a 422 can also be the
          // error page (expired CSRF token), which must never be rendered as a
          // stream — Turbo would append the whole page to <html>.
          const isStream = (xhr.getResponseHeader("Content-Type") || "").includes("text/vnd.turbo-stream.html")
          reject(this.#uploadError(`${xhr.status}`, xhr.status, isStream ? xhr.responseText : null))
        }
      })

      // status 0 = the connection never produced a response (server restarted
      // mid-request during a deploy, wifi dropped) — the retryable case.
      xhr.addEventListener("error", () => reject(this.#uploadError("Network error", 0)))
      xhr.addEventListener("timeout", () => reject(this.#uploadError("Timeout", 0)))
      xhr.addEventListener("abort", () => reject(this.#uploadError("Aborted", null)))

      xhr.open("POST", uploadUrl)
      xhr.setRequestHeader("X-CSRF-Token", csrfToken)
      xhr.setRequestHeader("Accept", "text/vnd.turbo-stream.html")
      xhr.send(formData)
    })
  }

  // Show uploading state with a progress bar at 0%
  setItemUploading(previewItem) {
    const description = previewItem.querySelector(".attachments__description")
    if (description) {
      // Save file size text for showing after completion
      const sizeSpan = description.querySelectorAll("span")[1]
      if (sizeSpan) {
        previewItem.dataset.fileSize = sizeSpan.textContent
      }
      description.innerHTML =
        '<div class="attachments__progress">' +
        '<div class="attachments__progress-bar" style="width: 0%; left: 0;"></div>' +
        "</div>"
    }
    // Hide remove button during upload
    const removeBtn = previewItem.querySelector(".attachments__remove")
    if (removeBtn) removeBtn.style.display = "none"
  }

  // Update the progress bar width
  updateItemProgress(previewItem, percent) {
    const progressBar = previewItem.querySelector(".attachments__progress-bar")
    if (progressBar) {
      progressBar.style.width = `${percent}%`
      progressBar.style.left = "0"
    }
  }

  // Show completed state with check icon
  setItemComplete(previewItem) {
    previewItem.classList.add("attachments__item--complete")
    const icon = previewItem.querySelector(".attachments__icon > span")
    if (icon) {
      icon.textContent = "check_circle"
      icon.style.color = "var(--lc-brand, #6750A4)"
    }
    // Restore file size from original data, remove progress bar
    const description = previewItem.querySelector(".attachments__description")
    if (description) {
      const fileSize = previewItem.dataset.fileSize
      description.innerHTML = fileSize ? `<span>${fileSize}</span>` : ""
    }
  }

  // Show "retrying" state between two automatic attempts
  setItemRetrying(previewItem) {
    const description = previewItem.querySelector(".attachments__description")
    if (description) {
      description.innerHTML = `<span>${this.#escapeHtml(this.#texts.retryingText)}</span>`
    }
  }

  // Show error state with error icon and a per-file retry action
  setItemError(previewItem, { retryable = true } = {}) {
    previewItem.classList.add("attachments__item--error")
    const icon = previewItem.querySelector(".attachments__icon > span")
    if (icon) {
      if (!previewItem.dataset.originalIcon) {
        previewItem.dataset.originalIcon = icon.textContent
      }
      icon.textContent = "error"
      icon.style.color = "var(--lc-danger, #B80546)"
    }
    const description = previewItem.querySelector(".attachments__description")
    if (description) {
      const retryLink = retryable
        ? ` <button type="button" class="btn btn-link btn-sm p-0 align-baseline attachments__retry" ` +
          `data-action="click->attachments#retryItem">${this.#escapeHtml(this.#texts.retryText)}</button>`
        : ""
      description.innerHTML = `<span>${this.#escapeHtml(this.#texts.errorText)}</span>${retryLink}`
    }
  }

  // Undo the error state before re-uploading a file
  clearItemError(previewItem) {
    previewItem.classList.remove("attachments__item--error")
    const icon = previewItem.querySelector(".attachments__icon > span")
    if (icon && previewItem.dataset.originalIcon) {
      icon.textContent = previewItem.dataset.originalIcon
      icon.style.color = ""
    }
  }

  // Close the offcanvas sidebar (reset handled by hidden.bs.offcanvas listener)
  closeSidebar() {
    const offcanvasInstance = Offcanvas.getInstance(this.element)
    if (offcanvasInstance) {
      offcanvasInstance.hide()
    }
  }

  // Put the footer button back into its initial "create expenses" state
  restoreSubmitButton() {
    if (!this.hasSubmitButtonTarget) return

    this.submitButtonTarget.classList.remove("btn-warning")
    this.submitButtonTarget.classList.add("btn-primary")
    this.submitButtonTarget.innerHTML = this._originalButtonHTML || "Create"
    this.submitButtonTarget.setAttribute("data-action", "click->attachments#submitAndClear")
  }

  // Reset sidebar to initial state after closing
  resetSidebar() {
    // Closing mid-drag (Esc, backdrop) never fires the matching dragleave, so
    // without this the panel reopens already tinted.
    this.#setDragging(false)
    this._failedTasks = []
    this._completedCount = 0
    this._groupMode = false
    this.#renderGroupMode()
    this.restoreSubmitButton()
    if (this.hasSubmitButtonTarget) {
      this.submitButtonTarget.disabled = true
    }
    this.clearFiles()
  }

  // Clear files and reset UI
  clearFiles() {
    this._accumulatedFiles = new DataTransfer()
    // A new selection is a new group — reusing the token would resolve to the
    // previous group's expense and silently drop the new files.
    this._groupToken = null

    if (this.hasFilesOriginTarget) {
      this.filesOriginTarget.value = ""

      const hiddenInput = document.getElementById("hidden_attachments_input")
      if (hiddenInput) hiddenInput.value = ""

      if (this.hasAttachmentsPreviewTarget) {
        this.attachmentsPreviewTarget.innerHTML = ""
      }
      this.showUploadArea()
      this.updateSubmitButtonState()
    }
  }

  openFilePicker() {
    this.filesOriginTarget.click()
  }

  // Surface a dismissable alert when a 0-byte file is selected.
  showEmptyFileNotice(filename) {
    flashFileWarning(`${this.#texts.emptyFileText}${filename}`)
  }

  // Same, for a file whose type the input never advertised as supported —
  // reachable by dropping one, which no `accept` attribute can prevent.
  showUnsupportedFileNotice(filename) {
    flashFileWarning(`${this.#texts.unsupportedFileText}${filename}`)
  }

  // The accumulated selection lives in three places (the visible input, the main
  // form's input, the sidebar's hidden input). Whenever it changes — add, remove
  // or reorder — all three have to be rewritten, or the request posts a stale set.
  //
  // A changed selection is a DIFFERENT group, so the idempotency token is dropped
  // here too. Keeping it would make the server treat the new submission as a
  // resend of the old one and hand back the expense built from the previous files
  // — the added or reordered pages would vanish without a word.
  #syncFileInputs() {
    this._groupToken = null

    if (this.hasFilesOriginTarget) {
      this.filesOriginTarget.files = this._accumulatedFiles.files
    }
    if (this.hasAttachmentsTarget) {
      this.attachmentsTarget.files = this._accumulatedFiles.files
      return
    }

    const hiddenInput = document.getElementById("hidden_attachments_input")
    if (hiddenInput) {
      hiddenInput.files = this._accumulatedFiles.files
    }
  }

  #escapeHtml(text) {
    const div = document.createElement("div")
    div.textContent = text
    return div.innerHTML
  }

  // Idempotency token of a file, minted once and remembered for its lifetime.
  #tokenFor(file) {
    let token = this._uploadTokens.get(file)
    if (!token) {
      token = this.#newUploadToken()
      this._uploadTokens.set(file, token)
    }
    return token
  }

  // A fresh token. randomUUID needs a secure context, so keep a fallback —
  // a missing token silently disables duplicate protection.
  #newUploadToken() {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
      return crypto.randomUUID()
    }
    return `upl-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`
  }

  #uploadError(message, status, body = null) {
    const error = new Error(message)
    error.status = status
    error.body = body
    error.transient = status !== null && TRANSIENT_HTTP_STATUSES.includes(status)
    return error
  }

  #sleep(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms))
  }

  // Toggle extract data checkbox value in the form
  toggleExtractData(event) {
    const hiddenCheckbox = document.getElementById("extract_data_hidden")
    if (hiddenCheckbox) {
      hiddenCheckbox.checked = event.target.checked
    }
  }

  // Opened from a link (expenses?upload=1). Once only: a page restored from Turbo's cache, a reload or the Back
  // button must not pop the panel open again, so the value and the parameter go as soon as it has opened.
  #openOnce() {
    Offcanvas.getOrCreateInstance(this.element).show()
    this.openOnConnectValue = false
    const url = new URL(window.location.href)
    if (!url.searchParams.has("upload")) return

    url.searchParams.delete("upload")
    window.history.replaceState(window.history.state, "", url)
  }
}
